import { colors } from '@theme/foundations/colors';
import { ColorLabelEnum, hexColor } from '@utils/map/map.constants';
import { CategoryEnum, sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';

import { SensorOrSubSensorConfigurationByType } from '@/types/sensor/configuration.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';
import {
  AutomatonSegmentStatus,
  EffectiveSensorStatusEnum,
  MetricStatusEnum,
  SensorTypeEnum,
  SensorTypeEnumWithoutSection,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';
import { SensorOrSubSensorStatusByType } from '@/types/sensor/status.types';

export enum MonitoringLadTypeEnum {
  AD = 'AD',
  AER = 'AER',
  AVI = 'AVI',
  GCS = 'GCS',
  HLD = 'HLD',
  SID = 'SID',

  ARD = 'ARD',
  DSH = 'DSH',
  SKY = 'SKY',

  HLP = 'HLP',
  OVF = 'OVF',
  ZMR = 'ZMR',
  AXIS = 'AXIS',

  FLIR = 'FLIR',
  RMAX = 'RMAX',
  SPX = 'SPX',
  XTA = 'XTA',

  BLAST = 'BLAST',
  DFB6 = 'DFB6',
  M3B = 'M3B',
  SKJ = 'SKJ',

  GEN = 'GEN',
}

export enum MonitoringPerimTypeEnum {
  API = 'API',
  BT = 'BT',
  TRO = 'TRO',
}

export type SensorOrSubSensorInfoByType<T extends SensorTypeEnum | SubSensorTypeEnum> = {
  active: boolean;
  maintenance: boolean;
  type: T;
  status: T extends SubSensorTypeEnum.AUTOMATON_SEGMENT
    ? AutomatonSegmentStatus | null
    : SensorOrSubSensorStatusByType<T> | null;
  configuration: SensorOrSubSensorConfigurationByType<T>;
};

export type SensorOrSubSensorInfo = SensorOrSubSensorInfoByType<SensorTypeEnum | SubSensorTypeEnum>;
export type SensorOrSubSensorInfoWithoutSection = SensorOrSubSensorInfoByType<SensorTypeEnumWithoutSection>;

export type MonitoringSensorType = MonitoringLadTypeEnum | MonitoringPerimTypeEnum;

export const monitoringTypeFromSensorType: Record<SensorTypeEnum | SubSensorTypeEnum, MonitoringSensorType> = {
  ADSB: MonitoringLadTypeEnum.AD,
  AEROSCOPE: MonitoringLadTypeEnum.AER,
  ARDRONIS: MonitoringLadTypeEnum.ARD,
  AUTOMATON: MonitoringPerimTypeEnum.API,
  AVISO: MonitoringLadTypeEnum.AVI,
  BLAST: MonitoringLadTypeEnum.BLAST,
  DFB6: MonitoringLadTypeEnum.DFB6,
  DRONE_SHIELD: MonitoringLadTypeEnum.DSH,
  FLIR: MonitoringLadTypeEnum.FLIR,
  GENERIC: MonitoringLadTypeEnum.GEN,
  GROUND_STATION: MonitoringLadTypeEnum.GCS,
  HOLODRONE: MonitoringLadTypeEnum.HLD,
  AF2206: MonitoringLadTypeEnum.HLP,
  AXIS: MonitoringLadTypeEnum.AXIS,
  MAJES: MonitoringLadTypeEnum.M3B,
  ONVIF: MonitoringLadTypeEnum.OVF,
  ROBIN_MAX: MonitoringLadTypeEnum.RMAX,
  SKY_EYE: MonitoringLadTypeEnum.SKY,
  SKY_JACKER: MonitoringLadTypeEnum.SKJ,
  SPEXER: MonitoringLadTypeEnum.SPX,
  XENTA: MonitoringLadTypeEnum.XTA,
  ZMER: MonitoringLadTypeEnum.ZMR,
  SENID: MonitoringLadTypeEnum.SID,
  ARDRONIS_ANTENNA: MonitoringLadTypeEnum.ARD,
  AUTOMATON_CABINET: MonitoringPerimTypeEnum.BT,
  AUTOMATON_SEGMENT: MonitoringPerimTypeEnum.TRO,
  GENERIC_SUB_SENSOR: MonitoringLadTypeEnum.GEN,
  ROBIN_MAX_RADAR: MonitoringLadTypeEnum.RMAX,
  SKY_EYE_ANTENNA: MonitoringLadTypeEnum.SKY,
  SKY_JACKER_ANTENNA: MonitoringLadTypeEnum.SKJ,
  SPEXER_RADAR: MonitoringLadTypeEnum.SPX,
  SENID_ANTENNA: MonitoringLadTypeEnum.SID,
} as const;

export const categoryFromSensorFamily: Record<SensorFamilyEnum, CategoryEnum> = {
  [SensorFamilyEnum.AUTOMATON]: CategoryEnum.PERIM,
  [SensorFamilyEnum.CAMERA]: CategoryEnum.LAD,
  [SensorFamilyEnum.COLLABORATIVE]: CategoryEnum.LAD,
  [SensorFamilyEnum.NEUTRAL]: CategoryEnum.LAD,
  [SensorFamilyEnum.RADAR]: CategoryEnum.LAD,
  [SensorFamilyEnum.RF]: CategoryEnum.LAD,
  [SensorFamilyEnum.RID]: CategoryEnum.LAD,
} as const;

export const sensorFamilyFromMonitoringSensorType: Record<MonitoringSensorType, SensorFamilyEnum> = Object.fromEntries(
  Object.keys(monitoringTypeFromSensorType).map((key) => {
    const typeKey = key as SensorTypeEnum | SubSensorTypeEnum;
    const type = monitoringTypeFromSensorType[typeKey];
    const family = sensorFamilyFromSensorType[typeKey];
    return [type, family];
  }),
) as Record<MonitoringSensorType, SensorFamilyEnum>;

// TODO HLF#30437 Remove or improve?
export enum CriticalityCategoryEnum {
  UNREACHABLE = 'UNREACHABLE',
  DEGRADED = 'DEGRADED',
  MAINTENANCE = 'MAINTENANCE',
  SLEEP = 'SLEEP',
  INACTIVE = 'INACTIVE',
  OK = 'OK',
}

export const criticalityOrder = [
  CriticalityCategoryEnum.UNREACHABLE,
  CriticalityCategoryEnum.DEGRADED,
  CriticalityCategoryEnum.MAINTENANCE,
  CriticalityCategoryEnum.SLEEP,
  CriticalityCategoryEnum.OK,
  CriticalityCategoryEnum.INACTIVE,
] as const;

export const criticalityCategoryFromSensorStatus: Record<
  EffectiveSensorStatusEnum | SpecificStateEnum.SLEEP,
  CriticalityCategoryEnum
> = {
  KO: CriticalityCategoryEnum.DEGRADED,
  DEGRADED: CriticalityCategoryEnum.DEGRADED,
  HEALTHY: CriticalityCategoryEnum.OK,
  SLEEP: CriticalityCategoryEnum.SLEEP,
  UNREACHABLE: CriticalityCategoryEnum.UNREACHABLE,
} as const;

export const statusCategoryColor: Record<CriticalityCategoryEnum, string> = {
  [CriticalityCategoryEnum.INACTIVE]: hexColor[ColorLabelEnum.WHITE],
  [CriticalityCategoryEnum.MAINTENANCE]: hexColor[ColorLabelEnum.ORANGE],
  [CriticalityCategoryEnum.OK]: hexColor[ColorLabelEnum.GREEN],
  [CriticalityCategoryEnum.SLEEP]: hexColor[ColorLabelEnum.GRAY],
  [CriticalityCategoryEnum.DEGRADED]: hexColor[ColorLabelEnum.ORANGE],
  [CriticalityCategoryEnum.UNREACHABLE]: hexColor[ColorLabelEnum.PURPLE],
} as const;

export type AllMonitoringFilterEnum = CategoryEnum | SensorFamilyEnum | MonitoringSensorType;

export const metricStatusColor: Record<MetricStatusEnum, string> = {
  [MetricStatusEnum.CRITICAL]: colors.red.DEFAULT.value,
  [MetricStatusEnum.WARNING]: colors.orange.DEFAULT.value,
  [MetricStatusEnum.OK]: colors.green.DEFAULT.value,
} as const;

export type SensorOrSubSensorTypeEnum =
  | SensorTypeEnum
  | Exclude<SubSensorTypeEnum, SubSensorTypeEnum.AUTOMATON_SEGMENT>;
